import { useDispatch, useSelector } from 'react-redux';
import { selectLang } from '../../store/ui/ui.selector';
import { Table } from 'antd';
import { selectFarmsData } from '../../store/farms/farms.selector';
import { useCallback, useEffect, useState } from 'react';
import { translate } from '../../lib/lang.helper';
import {
  calcLineLastSize,
  lineLengthLabel,
  weightAmountLabel,
} from '../../lib/farm.helpers';
import {
  Button,
  Dropdown,
  Spinner,
  Subtitle,
  Title,
} from '../../components/shared';
import { formatNumber } from '../../entities/util-functions';
import { useHistory } from 'react-router-dom';
import { Bar } from 'react-chartjs-2';
import { downloadReport, sendSingleRequest } from '../../apis';
import { ChartData } from 'chart.js';
import {
  calcDiffDays,
  generateMonthlyRanges,
  generateWeeklyRanges,
  pcsToDoz,
  defaultDateFormat,
  toMillisecond,
  amountDays,
  displayValueUnit,
} from '../../lib/common.helpers';
import {
  IMusselGrowingCycle,
  IOysterGrowingCycle,
} from '../../entities/growing.entities';
import { TBusinessType } from '../../entities/general.entities';
import { selectAccount } from '../../store/auth/auth.selector';
import { APP_NAME } from '../../lib/constant';
import { ILineResource } from '../../entities/farms.entities';

interface IRow {
  id: number | string;
  week: string;
  date: string;
  farm_id?: number;
  farm_name: string;
  farm_number: string;
  line_id?: number;
  line: string;
  last_date: string;
  last_size: string;
  last_known_at: string;
  line_length: number | null;
  age: string;
  last_harvest: { amount: number; length: number | null } | null;
  estimated: React.ReactElement;
  estimated_amount: number | null;
  estimated_size: string;
  planned_date: string | null;
  rowSpan?: number;
  totalMussels?: number;
  totalOysters?: number;
  crop_type: TBusinessType;
  line_type: ILineResource['line_type'];
  farming_method: string | null;
}

interface ILineProp {
  farm_id: number;
  line_id: number;
  predicted_harvest_date: number;
  predicted_harvest_amount: number;
  target_shell_size: number;
}

const columns = (lang: any, unitType: any) => [
  {
    key: 'week',
    render: (x: IRow) =>
      x.rowSpan ? (
        <span>
          <div className='text-center'>{x.week}</div>
          <div>{x.date}</div>
        </span>
      ) : (
        ''
      ),
    width: 210,
  },
  {
    title: translate(lang, 'Farm'),
    key: 'farm',
    render: (x: IRow) => (
      <span>
        <div>{x.farm_name}</div>
        <div className='text-center'>{x.farm_number}</div>
      </span>
    ),
  },
  {
    title: translate(lang, 'Line'),
    key: 'line',
    render: (x: IRow) => <span>{x.line}</span>,
    width: 60,
  },
  {
    title: translate(lang, 'Last assessment date'),
    key: 'last_date',
    render: (x: IRow) => <span>{x.last_date}</span>,
  },
  {
    title: translate(lang, 'Last known size'),
    key: 'last_size',
    render: (x: IRow) => (
      <span>
        <div>{x.last_size}</div>
        <div style={{ color: 'olive' }}>{x.last_known_at}</div>
      </span>
    ),
  },
  {
    title: translate(lang, 'Longline length'),
    key: 'line_length',
    render: (x: IRow) => (
      <span>
        {lineLengthLabel(
          x.crop_type,
          x.line_type,
          x.farming_method,
          unitType,
          x.line_length,
        )}
      </span>
    ),
  },
  {
    title: translate(lang, "Today's age"),
    key: 'age',
    render: (x: IRow) => <span>{x.age}</span>,
  },
  {
    title: translate(lang, 'Previous harvest'),
    key: 'last_harvest',
    render: (x: IRow) => {
      if (!x.last_harvest) {
        return <span>-</span>;
      }
      return (
        <span>
          <div>
            {weightAmountLabel(
              x.crop_type,
              unitType,
              x.last_harvest.amount / 1000,
              'weight_t',
            )}
          </div>
          <div>
            {lineLengthLabel(
              x.crop_type,
              x.line_type,
              x.farming_method,
              unitType,
              x.last_harvest.length,
            )}
          </div>
        </span>
      );
    },
  },
  {
    title: translate(lang, 'Estimated harvest'),
    key: 'estimate_harvest',
    render: (x: IRow) => (
      <span>
        <div>{x.estimated}</div>
        {!!x.planned_date && (
          <div style={{ color: 'darkorange' }}>{x.planned_date}</div>
        )}
      </span>
    ),
  },
  {
    title: translate(lang, 'Total'),
    key: 'total',
    render: (x: IRow) => (
      <span>
        {!!x.totalMussels && (
          <div>
            {displayValueUnit(x.totalMussels / 1000, unitType, 'weight_t')}
          </div>
        )}
        {!!x.totalOysters && <div>{`${pcsToDoz(x.totalOysters)} dz`}</div>}
      </span>
    ),
  },
];

const HarvestPlannerPage = () => {
  const dispatch = useDispatch<any>();
  const history = useHistory();
  const lang = useSelector(selectLang);
  const farmsData = useSelector(selectFarmsData);
  const unitType = useSelector(selectAccount)?.display_unit;

  const [rowsData, setRowsData] = useState<IRow[]>();
  const [chartData, setChartData] =
    useState<ChartData<'bar', number[], string>>();
  const [totalManualMussels, setTotalManualMussels] = useState(0);
  const [selectedOwner, setSelectedOwner] = useState<string>();
  const [loading, setLoading] = useState(false);

  const totalMusselSum =
      rowsData?.reduce((p, c) => p + (c.totalMussels ?? 0), 0) ?? 0,
    totalOysterSum =
      rowsData?.reduce((p, c) => p + (c.totalOysters ?? 0), 0) ?? 0;
  const owners = farmsData
    .reduce(
      (prv, cur) => [...prv, cur.owners.map(o => o.title)].flat(),
      [] as string[],
    )
    .filter((v, i, a) => v && a.indexOf(v) === i);

  const downloadPDF = async () => {
    try {
      const res = await downloadReport(
        {
          content: rowsData,
          total_mussels: totalMusselSum,
          total_oysters: totalOysterSum,
          file_type: 'pdf',
        },
        'harvest-plan',
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      let url = window.URL.createObjectURL(blob);
      let link = document.createElement('a');
      link.href = url;
      link.download = `report.pdf`;
      link.click();
      window.URL.revokeObjectURL(link.href);
    } catch (error: any) {
      window.alert(error?.message ?? 'Unknown error');
    }
  };
  const downloadExcel = async () => {
    try {
      const res = await downloadReport(
        {
          content: rowsData,
          total_mussels: totalMusselSum,
          total_oysters: totalOysterSum,
          file_type: 'xlsx',
        },
        'harvest-plan',
      );
      let url = window.URL.createObjectURL(res.data);
      let link = document.createElement('a');
      link.href = url;
      link.download = `report.xlsx`;
      link.click();
      window.URL.revokeObjectURL(link.href);
    } catch (error: any) {
      window.alert(error?.message ?? 'Unknown error');
    }
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    const response = await sendSingleRequest(
      {},
      'GET',
      'api/farm/lines-props',
      true,
    );
    setLoading(false);
    if (!response.status) {
      window.alert(response.data?.message ?? 'Server error');
      return;
    }
    const linesProps: ILineProp[] = response.data;
    const weekRanges = generateWeeklyRanges();

    let result: IRow[] = [];
    let totalManualMussels = 0;
    for (const week of weekRanges) {
      let first = 0,
        cnt = 0,
        totalMussels = 0,
        totalOysters = 0;
      for (const farm of farmsData) {
        if (
          selectedOwner &&
          !farm.owners.some(o => o.title === selectedOwner)
        ) {
          continue;
        }
        for (const line of farm.lines) {
          const cycle = line.growing_cycle;
          if (!cycle) continue;

          const prop = linesProps.find(
            x => x.farm_id === farm.id && x.line_id === line.id,
          );
          if (!prop) continue;

          const shellSize = calcLineLastSize(line);
          if (!shellSize) continue;

          const lastKnownDate = calcDiffDays(
            line.growing_cycle?.last_assessment?.assessment_date ??
              line.growing_cycle?.main_seed.planned_date_seed ??
              0,
          );

          if (
            week.start_date <= toMillisecond(prop.predicted_harvest_date) &&
            toMillisecond(prop.predicted_harvest_date) <= week.finish_date
          ) {
            const id = result.length + 1;

            let lengthValue: IRow['line_length'] = null;
            if (line.crop_type === 'MUSSEL') {
              lengthValue = (cycle as IMusselGrowingCycle).current_line_length;
            } else if (line.crop_type === 'OYSTER') {
              lengthValue = (cycle as IOysterGrowingCycle).current_basket_count;
            }
            let manualAmt = cycle.last_assessment?.estimated_amount;
            let estimatedAmount = <span></span>;
            let est_size = '';
            if (line.crop_type === 'MUSSEL') {
              const est_amount = displayValueUnit(
                prop.predicted_harvest_amount / 1000,
                unitType,
                'weight_t',
              );
              est_size = displayValueUnit(
                prop.target_shell_size,
                unitType,
                'size_mm',
              );
              estimatedAmount = (
                <>
                  <div>{est_amount}</div>
                  {!!manualAmt && (
                    <div className='color-red tx-tiny'>
                      {displayValueUnit(manualAmt / 1000, unitType, 'weight_t')}
                    </div>
                  )}
                  <div className='color-darkcyan tx-tiny'>{est_size}</div>
                </>
              );
              if (manualAmt) {
                totalManualMussels += manualAmt;
              }
            } else if (line.crop_type === 'OYSTER') {
              const est_amount = `${pcsToDoz(prop.predicted_harvest_amount)} dz`;
              est_size = displayValueUnit(
                prop.target_shell_size,
                unitType,
                'size_mm',
              );
              estimatedAmount = (
                <>
                  <div>{est_amount}</div>
                  {!!manualAmt && (
                    <div className='color-red tx-tiny'>
                      {`${formatNumber(manualAmt / 12)} dz`}
                    </div>
                  )}
                  <div className='color-darkcyan tx-tiny'>{est_size}</div>
                </>
              );
            }

            result.push({
              id: id,
              week: week.week_label,
              date: week.day_label,
              farm_id: farm.id,
              line_id: line.id,
              farm_name: farm.name,
              farm_number: farm.farm_number,
              line: line.line_name,
              last_date: defaultDateFormat(
                cycle.last_assessment?.assessment_date,
                '-',
              ),
              last_size: displayValueUnit(shellSize.avg, unitType, 'size_mm'),
              last_known_at: translate(lang, '%s ago', amountDays(lastKnownDate, lang)),
              line_length: lengthValue,
              age: amountDays(
                calcDiffDays(cycle.main_seed.planned_date_seed),
                lang,
              ),
              last_harvest: line.last_final_harvest
                ? {
                    amount: line.last_final_harvest.amount,
                    length: line.last_final_harvest.line_length,
                  }
                : null,
              estimated: estimatedAmount,
              estimated_amount: prop.predicted_harvest_amount,
              estimated_size: est_size,
              planned_date: defaultDateFormat(
                cycle.last_assessment?.planned_date_harvest ??
                  cycle.main_seed.planned_date_harvest,
              ),
              crop_type: line.crop_type,
              line_type: line.line_type,
              farming_method: line.farming_method ?? null,
            });
            if (!first) first = id;
            if (line.crop_type === 'MUSSEL') {
              totalMussels += prop.predicted_harvest_amount;
            } else if (line.crop_type === 'OYSTER') {
              totalOysters += prop.predicted_harvest_amount;
            }
            cnt++;
          }
        }
      }
      if (first) {
        let i = result.findIndex(x => x.id === first);
        result[i].rowSpan = cnt;
        result[result.length - 1].totalMussels = totalMussels;
        result[result.length - 1].totalOysters = totalOysters;
      } else {
        result.push({
          id: result.length + 1,
          week: week.week_label,
          date: week.day_label,
          farm_name: '',
          farm_number: '',
          line: '',
          last_date: '',
          last_size: '',
          last_known_at: '',
          line_length: null,
          age: '',
          last_harvest: null,
          estimated_amount: null,
          estimated_size: '',
          estimated: <></>,
          planned_date: null,
          rowSpan: 1,
          crop_type: 'MUSSEL',
          line_type: 'LONGLINE',
          farming_method: null,
        });
      }
    }
    setRowsData(result);
    setTotalManualMussels(totalManualMussels);

    const monthRanges = generateMonthlyRanges();
    let musselValues: number[] = [];
    let oysterValues: number[] = [];
    let chartLabels: string[] = [];
    for (const month of monthRanges) {
      let musselSum = 0,
        oysterSum = 0;
      for (const farm of farmsData) {
        for (const line of farm.lines) {
          if (!line.growing_cycle) continue;
          const shellSize = calcLineLastSize(line);
          if (!shellSize) continue;

          const prop = linesProps.find(
            x => x.farm_id === farm.id && x.line_id === line.id,
          );
          if (!prop) continue;

          if (
            month.start_date <= toMillisecond(prop.predicted_harvest_date) &&
            toMillisecond(prop.predicted_harvest_date) <= month.finish_date
          ) {
            if (line.crop_type === 'MUSSEL') {
              musselSum += Math.round(prop.predicted_harvest_amount / 1000);
            } else if (line.crop_type === 'OYSTER') {
              oysterSum += pcsToDoz(prop.predicted_harvest_amount);
            }
          }
        }
      }
      chartLabels.push(month.month_label);
      musselValues.push(musselSum);
      oysterValues.push(oysterSum);
    }
    setChartData({
      labels: chartLabels,
      datasets: [
        {
          label: 'Planned harvest of Mussels',
          data: musselValues,
          backgroundColor: 'rgba(54, 162, 235, 0.2)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1,
        },
        {
          label: 'Planned harvest of Oysters',
          data: oysterValues,
          backgroundColor: 'rgba(80, 235, 9, 0.2)',
          borderColor: 'rgb(4, 230, 76)',
          borderWidth: 1,
        },
      ],
    });
  }, [dispatch, selectedOwner, farmsData, unitType]);

  useEffect(() => {
    loadData();
  }, [loadData, selectedOwner]);

  return (
    <div className='bg-secondary'>
      <div className='container harvest-planner-page pb-32'>
        <div className='overview d-flex justify-between align-center'>
          <Title size={5} color='black' align='default' fontWeight={700}>
            {translate(lang, 'Harvest planner')}
          </Title>
          <div className='d-flex'>
            <Button
              className='mr-7'
              size={3}
              type='fill'
              width='small'
              color='blue'
              onClick={downloadPDF}
            >
              {translate(lang, 'Download PDF')}
            </Button>
            <Button
              className='ml-7'
              size={3}
              type='fill'
              width='small'
              color='green'
              onClick={downloadExcel}
            >
              {translate(lang, 'Download Excel')}
            </Button>
          </div>
        </div>
        {rowsData === undefined ? (
          <div className='loader'>
            <Spinner />
          </div>
        ) : (
          <div className='content mb-16'>
            {chartData !== undefined && (
              <div className='mb-24 pr-16 pl-16 pt-16 pb-16'>
                <Bar
                  data={chartData}
                  options={{
                    responsive: true,
                    plugins: {
                      legend: {
                        display: false,
                      },
                    },
                    scales: {
                      x: {
                        beginAtZero: true,
                      },
                    },
                  }}
                  height={70}
                />
              </div>
            )}
            <div className='mt-17 mb-17 d-flex align-end'>
              <div style={{ width: '320px' }}>
                <Dropdown
                  label={translate(lang, 'Filter by owner')}
                  options={owners.map(o => ({ id: o, value: o, label: o }))}
                  value={selectedOwner}
                  onChange={v => setSelectedOwner(v ? v : undefined)}
                  showSearch={true}
                  allowClear={true}
                />
              </div>
              {loading && (
                <div className='ml-24'>
                  <Spinner position='local' />
                </div>
              )}
            </div>
            <Table
              rowKey={'id'}
              columns={columns(lang, unitType)}
              dataSource={rowsData}
              pagination={false}
              onRow={row => ({
                onClick: () =>
                  row.farm_id && row.line
                    ? history.push(`/farms/${row.farm_id}/${row.line_id}`)
                    : undefined,
                style: { cursor: 'pointer' },
              })}
              scroll={{ y: 600 }}
            />
            <TotalView
              lang={lang}
              unitType={unitType}
              totalMusselSum={totalMusselSum}
              totalOysterSum={totalOysterSum}
              totalManualMussels={totalManualMussels}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const TotalView: React.FC<{
  totalMusselSum: number;
  totalManualMussels: number;
  totalOysterSum: number;
  lang: any;
  unitType: any;
}> = ({
  totalMusselSum,
  totalManualMussels,
  totalOysterSum,
  lang,
  unitType,
}) => (
  <div
    className='mr-32 mt-16 mb-7'
    style={{ width: '450px', marginLeft: 'auto' }}
  >
    <div className='mb-8'>
      <Subtitle size={4} color='black' align='left' fontWeight={600}>
        {translate(lang, 'Total')}:
      </Subtitle>
    </div>
    {totalMusselSum > 0 && (
      <div className='mb-4'>
        <span
          style={{
            color: '#666',
            fontSize: '14px',
            marginRight: '8px',
          }}
        >
          {translate(lang, 'Calculated by %s:', APP_NAME)}
        </span>
        <span style={{ fontWeight: 500, fontSize: '16px' }}>
          {displayValueUnit(totalMusselSum / 1000, unitType, 'weight_t')}
          {' of Mussels'}
        </span>
      </div>
    )}
    {totalManualMussels > 0 && (
      <div className='mb-4'>
        <span
          style={{
            color: '#666',
            fontSize: '14px',
            marginRight: '8px',
          }}
        >
          {translate(lang, 'Calculated manually by farmers:')}
        </span>
        <span
          style={{
            fontWeight: 500,
            fontSize: '16px',
            color: '#d32f2f',
          }}
        >
          {displayValueUnit(totalManualMussels / 1000, unitType, 'weight_t')}
          {' of Mussels'}
        </span>
      </div>
    )}
    {totalOysterSum > 0 && (
      <div className='mb-4'>
        <span
          style={{
            color: '#666',
            fontSize: '14px',
            marginRight: '8px',
          }}
        >
          {translate(lang, 'Calculated by %s:', APP_NAME)}
        </span>
        <span style={{ fontWeight: 500, fontSize: '16px' }}>
          {`${pcsToDoz(totalOysterSum)} dz of Oysters`}
        </span>
      </div>
    )}
  </div>
);

export default HarvestPlannerPage;
